/**
 * Presentation-only composer for Player Home “What should I do next?”
 * Uses existing dashboard / ranking / room / notification / browse shapes — no services.
 */

import { formatMatchWhenShort } from "../../../domain/profile/profileDashboardStats";
import { ENTRY_STATUSES } from "../../../domain/competition/entryManagementTypes";

/**
 * @typedef {{
 *   id: string;
 *   kind: string;
 *   priority: number;
 *   title: string;
 *   detail?: string;
 *   href?: string;
 *   ctaLabel?: string;
 * }} JourneyItem
 */

/**
 * @param {{
 *   resumeRoom?: { room?: string; roomDisplayName?: string } | null;
 *   registrations?: { current?: object[]; upcoming?: object[] };
 *   ranking?: object | null;
 *   achievements?: object[];
 *   recentMatches?: object[];
 *   stats?: object;
 *   suggestedCompetitions?: object[];
 *   notifications?: object[];
 * }} input
 * @returns {JourneyItem[]}
 */
export function composeTodaysJourney(input = {}) {
  /** @type {JourneyItem[]} */
  const items = [];
  const regs = input.registrations || {};
  const current = regs.current || [];
  const upcoming = regs.upcoming || [];

  if (input.resumeRoom?.room) {
    items.push({
      id: "resume-room",
      kind: "resume",
      priority: 10,
      title: "Resume your match room",
      detail: input.resumeRoom.roomDisplayName || input.resumeRoom.room,
      href: `/room/${encodeURIComponent(input.resumeRoom.room)}/session?tab=players`,
      ctaLabel: "Resume",
    });
  }

  const pendingEntry = findPendingEntry(current, upcoming);
  if (pendingEntry) {
    items.push({
      id: `pending-${pendingEntry.competitionId}`,
      kind: "registration",
      priority: 20,
      title: "Registration awaiting approval",
      detail: pendingEntry.name,
      href: `/browse/${pendingEntry.competitionId}`,
      ctaLabel: "View",
    });
  }

  const running = current[0];
  if (running) {
    items.push({
      id: `running-${running.competitionId}`,
      kind: "competition",
      priority: 30,
      title: "Tournament in progress",
      detail: running.name,
      href: `/browse/${running.competitionId}`,
      ctaLabel: "Open",
    });
  }

  const nextUp = upcoming[0];
  if (nextUp && !running) {
    const closeHint = registrationCloseHint(nextUp.dates);
    items.push({
      id: `upcoming-${nextUp.competitionId}`,
      kind: "upcoming",
      priority: 40,
      title: closeHint || "Upcoming tournament",
      detail: nextUp.name,
      href: `/browse/${nextUp.competitionId}`,
      ctaLabel: "Details",
    });
  }

  const streak = input.stats?.currentWinningStreak ?? input.stats?.longestWinningStreak;
  if (streak && streak >= 2) {
    items.push({
      id: "streak",
      kind: "streak",
      priority: 50,
      title: `${streak}-match winning streak`,
      detail: "Keep it going on court",
      href: "/profile/matches",
      ctaLabel: "History",
    });
  }

  const rank = input.ranking?.profileStats?.currentRanking;
  if (rank != null) {
    items.push({
      id: "ranking",
      kind: "ranking",
      priority: 60,
      title: `Current ranking #${rank}`,
      detail:
        input.ranking?.profileStats?.seasonPoints != null
          ? `${input.ranking.profileStats.seasonPoints} season points`
          : "View leaderboard",
      href: "/rankings",
      ctaLabel: "Rankings",
    });
  }

  const ach = (input.achievements || [])[0];
  if (ach) {
    items.push({
      id: `ach-${ach.id}`,
      kind: "achievement",
      priority: 70,
      title: ach.title || "Achievement unlocked",
      detail: ach.detail,
      href: "/profile",
      ctaLabel: "Career",
    });
  }

  const unread = (input.notifications || []).find((n) => !n.read);
  if (unread) {
    items.push({
      id: `note-${unread.notificationId}`,
      kind: "notification",
      priority: 25,
      title: unread.title || "New notification",
      detail: unread.body,
      href: unread.href || undefined,
      ctaLabel: "Open",
    });
  }

  if (!items.length && (input.suggestedCompetitions || []).length) {
    const s = input.suggestedCompetitions[0];
    items.push({
      id: `suggest-${s.competitionId}`,
      kind: "discover",
      priority: 80,
      title: "Find your next tournament",
      detail: s.name,
      href: `/browse/${s.competitionId}`,
      ctaLabel: "Discover",
    });
  }

  if (!items.length) {
    items.push({
      id: "default",
      kind: "default",
      priority: 100,
      title: "Ready for your next rally?",
      detail: "Create an arena or browse open tournaments",
      href: "/arena?create=1",
      ctaLabel: "Create Arena",
    });
  }

  return items.sort((a, b) => a.priority - b.priority).slice(0, 5);
}

/**
 * Next-action card for home — competition registration or resume; not a live fixture feed.
 * @returns {object | null}
 */
export function composeNextActionCard({ resumeRoom, registrations, displayName } = {}) {
  if (resumeRoom?.room) {
    return {
      kind: "resume",
      badge: "Resume room",
      title: resumeRoom.roomDisplayName || "Match arena",
      subtitle: displayName ? `${displayName} · in session` : "Continue scoring",
      meta: resumeRoom.room,
      href: `/room/${encodeURIComponent(resumeRoom.room)}/session?tab=players`,
      ctaLabel: "Resume",
      countdownLabel: null,
      countdownValue: null,
    };
  }

  const running = registrations?.current?.[0];
  if (running) {
    return {
      kind: "competition",
      badge: "In progress",
      title: running.name,
      subtitle: (running.eventNames || []).join(", ") || "Your tournament",
      meta: statusLabel(running.status),
      href: `/browse/${running.competitionId}`,
      ctaLabel: "View tournament",
      countdownLabel: null,
      countdownValue: null,
    };
  }

  const up = registrations?.upcoming?.[0];
  if (up) {
    const start = up.dates?.startMs;
    return {
      kind: "upcoming",
      badge: "Upcoming",
      title: up.name,
      subtitle: (up.eventNames || []).join(", ") || registrationCloseHint(up.dates) || "Registered",
      meta: formatCompetitionWhen(start),
      href: `/browse/${up.competitionId}`,
      ctaLabel: "View",
      countdownLabel: start ? "Starts" : null,
      countdownValue: start ? formatCountdown(start) : null,
    };
  }

  return null;
}

/**
 * Season ring % from win rate when matches exist; otherwise null.
 */
export function seasonProgressPercent(stats) {
  const season = stats?.season;
  if (!season || !season.matches) return null;
  if (season.winRate != null) return Math.round(season.winRate);
  const total = (season.wins || 0) + (season.losses || 0);
  if (!total) return null;
  return Math.round(((season.wins || 0) / total) * 100);
}

export function formatRecentResultMeta(card) {
  if (!card) return "";
  const when = formatMatchWhenShort(card.completedAt);
  const score = card.scoreSummary || "";
  return [when, score].filter(Boolean).join(" · ");
}

function findPendingEntry(current, upcoming) {
  const all = [...(current || []), ...(upcoming || [])];
  for (const row of all) {
    const pending = (row.entries || []).find(
      (e) => e.status === ENTRY_STATUSES.PENDING || e.status === "pending"
    );
    if (pending) return row;
  }
  return null;
}

function registrationCloseHint(dates) {
  const close = dates?.registrationCloseMs;
  if (!close) return null;
  const days = Math.ceil((close - Date.now()) / 86400000);
  if (days < 0) return "Registration closed";
  if (days === 0) return "Registration closes today";
  if (days === 1) return "Registration closes tomorrow";
  if (days <= 7) return `Registration closes in ${days} days`;
  return null;
}

function formatCompetitionWhen(ms) {
  if (!ms) return "";
  return new Date(ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatCountdown(targetMs) {
  const diff = targetMs - Date.now();
  if (diff <= 0) return "Now";
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(hours / 24);
  if (days >= 1) return `${days}d`;
  if (hours >= 1) return `${hours}h`;
  const mins = Math.max(1, Math.floor(diff / 60000));
  return `${mins}m`;
}

function statusLabel(status) {
  if (!status) return "";
  return String(status).replace(/_/g, " ");
}
